import { useCallback, useEffect, useState, useRef, useMemo } from "react";

import api from "api/api";
import PropTypes from "prop-types";

import { AgGridReact } from "ag-grid-react"; // React Data Grid Component

// mui components
import Button from "@mui/material/Button";

// mui icons
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";

// component
import StatementsAddUpdateComponent from "./StatementsAddUpdateComponent";
import DataViewSelector from "./DataViewSelector";
import ClaimsDataDeleteComponent from "../common/ClaimsDataDeleteComponent";
import Loader from "../common/Loader";
import AgGridCheckboxFilter from "components/common/AgGridCheckboxFilter";

import { useDispatch, useSelector } from "react-redux";
import { loadClaimsMappingSelectedTagsFilterState, loadTagsState } from "store/features/claimsMappingSlice"; // Import loadTagsState
import { showNotification } from "store/features/notificationSlice";

const dataViewOptions = [
    { value: "1", label: "Claims Across Studies" },
    { value: "0", label: "Claims Not Present in Studies" },
];

function Statements({ selectedStudyType = {}, selectedStudyTypeMethod = {} }) {
    const gridRef = useRef();
    const dispatch = useDispatch();

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const tagsData = useSelector((state) => state.claimsMappingStore.tags)?.map((datum) => ({ value: datum.id, label: datum.title })) || [];
    const productCategoryId = productCategoryData?.value || null;

    const [allRowData, setAllRowData] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [claimsTerritoryTags, setClaimsTerritoryTags] = useState([]);
    const [claimsTerritorySelectedTags, setClaimsTerritorySelectedTags] = useState([]);

    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedRowToUpdate, setSelectedRowToUpdate] = useState([]);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const studyTypeMethods = [...new Set(selectedStudyTypeMethod.map((datum) => datum.value))];

    // Data View Toggle
    const [dataView, setDataView] = useState(dataViewOptions[0]);

    const fetchTagsData = useCallback(async () => {
        if (selectedStudyType?.value && studyTypeMethods.length > 0) {
            let url = process.env.REACT_APP_SERVER_PATH + `/claims-tags/all/`;
            if (productCategoryId) {
                url += `?product_category=${productCategoryId}`;
            }
            if (selectedStudyType?.value) {
                url += `&study_type=${selectedStudyType.value}`;
            }
            if (studyTypeMethods.length > 0) {
                url += `&study_type_methods=${studyTypeMethods.join(",")}`;
            }
            try {
                await api
                    .get(url)
                    .then(async (res) => {
                        await dispatch(loadTagsState(res.data));
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                    });
            } catch (error) {
                handleError("error", error);
            }
        }
    }, [dispatch, productCategoryId, selectedStudyType?.value, selectedStudyTypeMethod]);

    useEffect(() => {
        fetchTagsData(); // Call the fetch function when the component mounts or productCategoryId changes
    }, [fetchTagsData]);

    useEffect(() => {
        const arr = Array.from(
            new Set(
                allRowData
                    .map((ele) => ele.claims_tag_mapping__title)
                    .flat()
                    .sort()
            )
        );
        setClaimsTerritoryTags(arr);
    }, [allRowData]);

    useEffect(() => {
        let _allRows = [...allRowData];
        if (dataView.value === "0") {
            // Filter to show only claims not present in any study
            const filteredData = _allRows.filter((item) => item?.study_reference__title.length === 0) || [];
            _allRows = [...filteredData];
        } else {
            const filteredData = _allRows.filter((item) => item?.study_reference__title.length !== 0) || [];
            _allRows = [...filteredData];
        }

        if (claimsTerritorySelectedTags.length !== 0) {
            // works as or operator
            const filteredData = _allRows.filter((item) => item?.claims_tag_mapping__title.some((tag) => claimsTerritorySelectedTags.includes(tag))) || [];
            _allRows = [...filteredData];
        } else {
            _allRows = [..._allRows];
        }
        setRowData([..._allRows]);
        setSelectedRows([]);
    }, [claimsTerritorySelectedTags, allRowData, dataView]);

    const getSelectedTags = (values) => {
        setClaimsTerritorySelectedTags([...values]);
        dispatch(loadClaimsMappingSelectedTagsFilterState(values));
    };

    const tagsCellRenderer = (params) => {
        if (Array.isArray(params.value) && params.value.length > 0) {
            return (
                <div>
                    {params.value.map((val, i) => {
                        return (
                            <span
                                key={i}
                                style={{
                                    background: "#E5E4E2",
                                    margin: "0px 3px",
                                    padding: "0px 10px",
                                    borderRadius: 5,
                                    lineHeight: "25px",
                                    display: "inline-block",
                                }}
                            >
                                {val}
                            </span>
                        );
                    })}
                </div>
            );
        } else {
            return params.value;
        }
    };

    const multipleCellRenderer = (params) => {
        if (Array.isArray(params.value) && params.value.length > 0) {
            return (
                <div className="ag-multi-line-cell-render">
                    {params.value.map((val, i) => {
                        return <p key={i}>{val}</p>;
                    })}
                </div>
            );
        } else {
            return params.value;
        }
    };

    const handleUpdateClick = (row) => {
        setSelectedRowToUpdate(row.data);
        setIsUpdateDialogOpen(true);
    };

    const columnDefs = [
        {
            headerName: "Backend Variable",
            field: "backend_variable",
            minWidth: 420,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
        },
        {
            headerName: "Claims Statements",
            field: "statement",
            autoHeight: true,
            minWidth: 720,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
            cellStyle: {
                color: "#005ffe",
            },
        },
        {
            headerName: "Claims Territory tags(s)",
            field: "claims_tag_mapping__title",
            minWidth: 380,
            cellRenderer: tagsCellRenderer,
            autoHeight: true,
            wrapText: true,
            floatingFilter: true,
            filter: AgGridCheckboxFilter,
            filterParams: {
                values: claimsTerritoryTags,
                getSelectedValues: getSelectedTags,
            },
        },
        {
            headerName: "Likert Scale",
            field: "likert_scale",
            minWidth: 250,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains", "equals"],
            },
            cellStyle: {
                textAlign: "center",
            },
        },
        {
            headerName: "Study Reference",
            field: "study_reference__title",
            minWidth: 520,
            cellRenderer: multipleCellRenderer,
            autoHeight: true,
            wrapText: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
        },
        {
            headerName: "Action",
            // field: "claims_tag_mapping__title",
            minWidth: 120,
            cellRenderer: (row) => (
                <EditIcon
                    color="primary"
                    style={{
                        cursor: "pointer",
                    }}
                    onClick={() => handleUpdateClick(row)}
                />
            ),
        },
    ];

    const defaultColDef = useMemo(() => {
        return {
            editable: false,
            sortable: true,
            resizable: true,
            cellStyle: {
                fontSize: "14px",
                border: "none",
            },
            minWidth: 200,
        };
    }, []);

    const sortingOrder = useMemo(() => {
        return ["desc", "asc", null];
    }, []);

    const onSelectionChanged = useCallback(() => {
        let _selectedRows = gridRef.current.api.getSelectedRows();
        setSelectedRows(_selectedRows);
    }, []);

    const fetchData = useCallback(() => {
        if (selectedStudyType?.value && studyTypeMethods.length > 0) {
            setIsLoading(true);
            let url = process.env.REACT_APP_SERVER_PATH + `/claims-mappings/all/`;
            if (productCategoryId) {
                url += `?product_category=${productCategoryId}`;
            }
            if (selectedStudyType?.value) {
                url += `&study_type=${selectedStudyType.value}`;
            }
            if (studyTypeMethods.length > 0) {
                url += `&study_type_methods=${studyTypeMethods.join(",")}`;
            }
            try {
                api.get(url)
                    .then(async (res) => {
                        setAllRowData([...res.data]);
                        setIsLoading(false);
                    })
                    .catch(async (error) => {
                        handleError("error", error);

                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);

                setIsLoading(false);
            }
        }
    }, [productCategoryId, selectedStudyType?.value, selectedStudyTypeMethod]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        return () => {
            dispatch(loadClaimsMappingSelectedTagsFilterState([]));
        };
    }, [dispatch]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    return (
        <>
            {isLoading && <Loader />}
            <div
                className="ag-theme-alpine"
                style={{
                    height: "calc(100vh - 295px)",
                    width: "100%",
                    position: "relative",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        top: "-40px",
                        right: "0px",
                        display: "flex",
                        // alignItems: "center",
                    }}
                >
                    <DataViewSelector
                        value={dataView}
                        setValue={setDataView}
                        options={dataViewOptions}
                    />
                    <Button
                        size="small"
                        className="custom-btn custom-btn-outlined color-blue me-2"
                        variant="outlined"
                        startIcon={<ControlPointOutlinedIcon />}
                        onClick={() => setIsAddDialogOpen(true)}
                    >
                        Add
                    </Button>
                    <Button
                        size="small"
                        className="custom-btn"
                        variant="outlined"
                        startIcon={<DeleteOutlinedIcon />}
                        style={selectedRows.length === 0 ? { color: "rgba(0, 0, 0, 0.26)", borderColor: "rgba(0, 0, 0, 0.12)" } : { color: "red", borderColor: "red" }}
                        onClick={() => setIsDeleteDialogOpen(true)}
                        disabled={selectedRows.length === 0}
                    >
                        Delete
                    </Button>
                </div>
                <style>
                    {`
                        .ag-header-group-cell-label {
                            justify-content: center !important;
                        }
                        .ag-header-cell-comp-wrapper {
                            justify-content: center !important;
                        }
                    `}
                </style>
                <AgGridReact
                    rowStyle={{
                        background: "white",
                    }}
                    ref={gridRef}
                    rowSelection="multiple"
                    rowMultiSelectWithClick={true}
                    defaultColDef={defaultColDef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    onGridReady={(params) => params.id}
                    onSelectionChanged={onSelectionChanged}
                    // sideBar={false}
                    rowHeight={35}
                    headerHeight={40}
                    floatingFiltersHeight={40}
                    suppressDragLeaveHidesColumns={true}
                    sortingOrder={sortingOrder}
                ></AgGridReact>
            </div>
            <div className="d-flex">
                <span
                    style={{
                        fontSize: 12,
                        color: "#495057",
                        marginRight: 20,
                        fontWeight: 600,
                    }}
                >
                    Selected rows: {selectedRows.length}
                </span>
                <span
                    style={{
                        fontSize: 12,
                        color: "#495057",
                        fontWeight: 600,
                    }}
                >
                    Total rows: {rowData.length}
                </span>
            </div>
            {isAddDialogOpen && (
                <StatementsAddUpdateComponent
                    title="Add New Claims Statement"
                    type="add"
                    tagsData={tagsData}
                    isOpen={isAddDialogOpen}
                    setIsAddDialogOpen={setIsAddDialogOpen}
                    fetchData={fetchData}
                    dataView={dataView}
                    selectedStudyType={selectedStudyType}
                    selectedStudyTypeMethod={selectedStudyTypeMethod}
                />
            )}
            {isUpdateDialogOpen && (
                <StatementsAddUpdateComponent
                    title="Update Claims Statement"
                    type="update"
                    tagsData={tagsData}
                    selectedRows={selectedRowToUpdate}
                    isOpen={isUpdateDialogOpen}
                    setIsUpdateDialogOpen={setIsUpdateDialogOpen}
                    fetchData={fetchData}
                    dataView={dataView}
                    selectedStudyType={selectedStudyType}
                    selectedStudyTypeMethod={selectedStudyTypeMethod}
                />
            )}
            {isDeleteDialogOpen && (
                <ClaimsDataDeleteComponent
                    title="The following Claims statements are already present in the studies :"
                    component="statements"
                    selectedRows={selectedRows}
                    isOpen={isDeleteDialogOpen}
                    setIsDeleteDialogOpen={setIsDeleteDialogOpen}
                    fetchData={fetchData}
                />
            )}
        </>
    );
}
// Typechecking props for the Statements
Statements.propTypes = {
    selectedStudyType: PropTypes.obj,
    selectedStudyTypeMethod: PropTypes.obj,
};

export default Statements;
